import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';
import GeckoRequest from 'services/gecko-request';
import { GeckoToastService, GECKO_CONSTS } from 'ui';
import { resetRatingsUsingConversationFilters } from '../../actions';

const ratingEmojis = {
  5: GECKO_CONSTS.RATINGS_TYPE_LOVE,
  4: GECKO_CONSTS.RATINGS_TYPE_HAPPY,
  3: GECKO_CONSTS.RATINGS_TYPE_NEUTRAL,
  2: GECKO_CONSTS.RATINGS_TYPE_SAD,
  1: GECKO_CONSTS.RATINGS_TYPE_ANGRY,
};

const RATINGS_PER_PAGE = 10;
const setToStartOfDay = (date) => date.startOf('day').toDate();
const setToEndOfDay = (date) => date.endOf('day').toDate();

export const useRatings = () => {
  const {
    conversationPredefinedDateRange: predefinedDateRange,
    conversationCustomDateEnabled: customDateRangeEnabled,
    conversationStartDateFilter: startDate,
    conversationEndDateFilter: endDate,
    filteredBots: bots,
    filteredChannels: channels,
    filteredRatings: ratings,
    filteredAgentsAndTeams: agentsAndTeams,
    ratingsShouldUseConversationFilters,
  } = useSelector((state) => state.statsRoute);
  const [filters, setFilters] = useState({
    startDate:
      (ratingsShouldUseConversationFilters && startDate) ||
      setToStartOfDay(moment().subtract(7, 'days')),
    endDate:
      (ratingsShouldUseConversationFilters && endDate) ||
      setToEndOfDay(moment()),
    ratings: (ratingsShouldUseConversationFilters && ratings) || [],
    bots: (ratingsShouldUseConversationFilters && bots) || [],
    channels: (ratingsShouldUseConversationFilters && channels) || [],
    agentsAndTeams:
      (ratingsShouldUseConversationFilters && agentsAndTeams) || [],
    page: 1,
    sortKey: 'createdAt',
    sortDirection: 'DESC',
  });
  const [feedback, setFeedback] = useState([]);
  const [pagination, setPagination] = useState({
    total_items: 0,
    current_page: 0,
    total_pages: 0,
  });

  const fetchRatings = async () => {
    try {
      const { agent: agentIds, team: teamIds } = filters.agentsAndTeams.reduce(
        (res, id) => {
          const [key, value] = id.split('_');
          res[key].push(Number(value));
          return res;
        },
        { agent: [], team: [] }
      );
      const res = await GeckoRequest.get('/stats/ratings', {
        startDate: String(filters.startDate),
        endDate: String(filters.endDate),
        rating_score: filters.ratings,
        bots: filters.bots,
        channels: filters.channels,
        agentIds,
        teamIds,
        limit: RATINGS_PER_PAGE,
        offset: (filters.page - 1) * RATINGS_PER_PAGE,
        order: [filters.sortKey, filters.sortDirection],
      });
      setFeedback(
        res.rows.map(({ rating_score, ...rest }) => ({
          ...rest,
          rating_score: ratingEmojis[rating_score],
        }))
      );
      setPagination({
        total_items: res.count,
        current_page: filters.page,
        total_pages: Math.ceil(res.count / RATINGS_PER_PAGE),
      });
    } catch (error) {
      new GeckoToastService().error(error);
    }
  };

  useEffect(() => {
    resetRatingsUsingConversationFilters();
  }, []);

  useEffect(() => {
    fetchRatings();
  }, [filters]);

  const handleFiltersChange = ({
    startDate,
    endDate,
    filteredRatings,
    filteredBots,
    filteredChannels,
    filteredAgents,
  }) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      startDate: setToStartOfDay(moment(startDate)),
      endDate: setToEndOfDay(moment(endDate)),
      ratings: filteredRatings,
      bots: filteredBots,
      channels: filteredChannels,
      agentsAndTeams: filteredAgents,
      page: 1,
    }));
  };

  const handleChangePage = (page) => {
    setFilters((prevFilters) => ({ ...prevFilters, page }));
  };

  const handleChangeOrder = (sortKey) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      page: 1,
      sortKey,
      sortDirection:
        prevFilters.sortKey === sortKey && prevFilters.sortDirection === 'DESC'
          ? 'ASC'
          : 'DESC',
    }));
  };

  const extraFilterProps = ratingsShouldUseConversationFilters
    ? {
        predefinedDateRange,
        customDateRangeEnabled,
        selectedBots: filters.bots,
        selectedChannels: filters.channels,
        selectedRatings: filters.ratings,
        selectedAgents: filters.agentsAndTeams,
      }
    : {};

  return {
    filters,
    feedback,
    pagination: {
      ...pagination,
      order_by: filters.sortKey,
      sort: filters.sortDirection,
      changePage: handleChangePage,
      changeOrder: handleChangeOrder,
    },
    handleFiltersChange,
    extraFilterProps,
  };
};
